/**
 * Period views over the Metric Trends series (quick-261013-hqb, John 2026-10-13:
 * "lets extend the metrics to cover ytd, looking at each aggregated quarter").
 *
 * FUNNEL_TRENDS is monthly. This module rolls those months up:
 *   - by quarter  → one point per calendar quarter (Adeptia FY = calendar year)
 *   - year to date → one pooled point over every month in the series
 * without touching the generator: every monthly point carries its numerator and
 * denominator, so a quarter is Σnum ÷ Σden for a rate or a mean, and the monthly
 * average for a volume (leads, conversations). Pure, no React, unit-tested.
 *
 * Caveat printed by the page: the monthly cycle point is a MEDIAN of days; the
 * quarter / YTD roll-up is Σdays ÷ wins, i.e. a mean.
 */
import type { FunnelTrends, TrendMonth, TrendPoint } from './funnel-trends';
import type { Model3yInputs } from './model3y';

/** The measurable series keys (everything on a TrendMonth except its labels). */
export type TrendKey = keyof Omit<TrendMonth, 'month' | 'partialMonth'>;

export type TrendPeriodMode = 'month' | 'quarter';

/**
 * How a metric rolls up across months:
 *   pooled   — Σnum ÷ Σden (rates, ratios, means)
 *   perMonth — average of the monthly values (volumes measured per month)
 */
export type TrendPoolKind = 'pooled' | 'perMonth';

export const TREND_POOL: Record<TrendKey, TrendPoolKind> = {
  leadToBooked: 'pooled',
  inboundDeliveredOfBooked: 'pooled',
  inboundMtgToOpp: 'pooled',
  conversationsPerMonth: 'perMonth',
  conversationsPerMeeting: 'pooled',
  outboundDeliveredOfDue: 'pooled',
  outboundMtgToOpp: 'pooled',
  newLogoShareOfOpps: 'pooled',
  inboundLeadsPerMonth: 'perMonth',
  conversationsPerSeller: 'perMonth',
  conversationsPerBdr: 'perMonth',
  avgCreatedOppValue: 'pooled',
  newLogoWinRate: 'pooled',
  avgWonValue: 'pooled',
  cycleDays: 'pooled',
};

export const TREND_KEYS = Object.keys(TREND_POOL) as TrendKey[];

/**
 * Which trend chart each three-year-model input reads (the "trend ↓" link beside
 * the box). Inputs with no monthly measurement are absent.
 */
export const TREND_FOR_INPUT: Partial<Record<keyof Model3yInputs, TrendKey>> = {
  inboundLeadsPerMonth: 'inboundLeadsPerMonth',
  leadToBooked: 'leadToBooked',
  inboundDeliveredOfBooked: 'inboundDeliveredOfBooked',
  inboundMtgToOpp: 'inboundMtgToOpp',
  conversationsPerSellerMonth: 'conversationsPerSeller',
  conversationsPerBdrMonth: 'conversationsPerBdr',
  conversationsPerMeeting: 'conversationsPerMeeting',
  outboundDeliveredOfBooked: 'outboundDeliveredOfDue',
  outboundMtgToOpp: 'outboundMtgToOpp',
  avgCreatedOppValue: 'avgCreatedOppValue',
  winRate: 'newLogoWinRate',
  avgWonValue: 'avgWonValue',
  cycleDays: 'cycleDays',
};

/** One period of the series: a month, a quarter or the year to date. Same point shape as a TrendMonth. */
export interface TrendPeriod extends TrendMonth {
  /** The months rolled into this period (one for month mode). */
  months: string[];
  /** 'month' | 'quarter' | 'ytd' — how `month` should be read. */
  kind: 'month' | 'quarter' | 'ytd';
}

/** '2026-07' → '2026-Q3'. */
export function quarterOf(month: string): string {
  const y = month.slice(0, 4);
  const m = Number(month.slice(5, 7));
  return `${y}-Q${String(Math.floor((m - 1) / 3) + 1)}`;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Short axis label: '2026-06' → 'Jun 26', '2026-Q3' → 'Q3 26', '2026' (ytd) → '2026 YTD'. */
export function periodLabel(p: Pick<TrendPeriod, 'month' | 'kind'>): string {
  if (p.kind === 'ytd') return `${p.month.slice(0, 4)} YTD`;
  if (p.kind === 'quarter') return `${p.month.slice(5)} ${p.month.slice(2, 4)}`;
  const m = Number(p.month.slice(5, 7));
  return `${MONTH_NAMES[m - 1] ?? p.month} ${p.month.slice(2, 4)}`;
}

/** Roll a list of monthly points into one. */
export function poolPoints(points: readonly TrendPoint[], kind: TrendPoolKind): TrendPoint {
  const num = points.reduce((t, p) => t + p.num, 0);
  const den = points.reduce((t, p) => t + p.den, 0);
  const immatureFlags = points.filter((p) => p.immature !== undefined);
  const immature = immatureFlags.length > 0 ? immatureFlags.some((p) => p.immature === true) : undefined;
  let out: TrendPoint;
  if (kind === 'pooled') {
    out = { value: den > 0 ? num / den : null, num, den };
  } else {
    const withData = points.filter((p) => p.value != null);
    const mean = withData.length > 0 ? withData.reduce((t, p) => t + (p.value ?? 0), 0) / withData.length : null;
    // keep a per-month denominator when every month shares one (sellers, 1); otherwise the sum (dials)
    const dens = new Set(points.map((p) => p.den));
    out = { value: mean, num, den: dens.size === 1 ? (points[0]?.den ?? 0) : den };
  }
  if (immature !== undefined) out.immature = immature;
  return out;
}

function rollUp(months: readonly TrendMonth[], label: string, kind: TrendPeriod['kind']): TrendPeriod {
  const out = {
    month: label,
    partialMonth: months.some((m) => m.partialMonth),
    months: months.map((m) => m.month),
    kind,
  } as TrendPeriod;
  for (const key of TREND_KEYS) {
    out[key] = poolPoints(
      months.map((m) => m[key]),
      TREND_POOL[key],
    );
  }
  return out;
}

/** The series by calendar quarter, in order; the quarter holding a partial month is partial. */
export function byQuarter(trends: Pick<FunnelTrends, 'series'>): TrendPeriod[] {
  const groups = new Map<string, TrendMonth[]>();
  for (const m of trends.series) {
    const q = quarterOf(m.month);
    const arr = groups.get(q) ?? [];
    arr.push(m);
    groups.set(q, arr);
  }
  return [...groups.entries()].map(([q, months]) => rollUp(months, q, 'quarter'));
}

/** The series month by month, as periods (identity + labels). */
export function byMonth(trends: Pick<FunnelTrends, 'series'>): TrendPeriod[] {
  return trends.series.map((m) => ({ ...m, months: [m.month], kind: 'month' as const }));
}

export function periods(trends: Pick<FunnelTrends, 'series'>, mode: TrendPeriodMode): TrendPeriod[] {
  return mode === 'quarter' ? byQuarter(trends) : byMonth(trends);
}

/** One pooled point over every month of the series' first calendar year (year to date). */
export function yearToDate(trends: Pick<FunnelTrends, 'series'>): TrendPeriod | null {
  const first = trends.series[0];
  if (!first) return null;
  const year = first.month.slice(0, 4);
  const months = trends.series.filter((m) => m.month.startsWith(year));
  return rollUp(months, year, 'ytd');
}
